import React from "react";
import { Box, FormControl, Input, Select, VStack, HStack, Button } from "@chakra-ui/react";

import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";
import {
	FieldGrid,
	Label,
	SectionHeader,
	commonInputProps,
	commonSelectProps,
	maybeErrorText,
	sectionCardProps,
} from "../matrimonyForm.ui";
import { EMPLOYMENT_SECTOR_OPTIONS, HIGHEST_EDUCATION_OPTIONS, SOCIAL_PLATFORM_OPTIONS } from "src/utils/matrimonyOptions";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";

type Props = {
	formData: MatrimonyFormData;
	errors: FormErrors;
	updateField: (field: keyof MatrimonyFormData, value: string) => void;
};

const Step2CareerEducation: React.FC<Props> = ({ formData, errors, updateField }) => {
	const links = Array.isArray(formData.socialMediaLinksList) ? formData.socialMediaLinksList : [];

	const setLinks = (next: Array<{ platform: string; url: string }>) => {
		updateField("socialMediaLinksList", next as any);
	};

	const addLink = () => {
		setLinks([...(links || []), { platform: "", url: "" }]);
	};

	const updateLink = (idx: number, patch: Partial<{ platform: string; url: string }>) => {
		setLinks(
			links.map((l, i) => (i === idx ? { ...l, ...patch } : l))
		);
	};

	const removeLink = (idx: number) => {
		setLinks(links.filter((_, i) => i !== idx));
	};

	return (
		<VStack align="stretch" spacing={{ base: 4, md: 6 }}>
			<Box {...sectionCardProps}>
				<SectionHeader title="EDUCATION DETAILS" />
				<Box mt={3}>
					<FieldGrid>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.highestEducation)}
						>
							<Label>{FIELD_LABELS.HIGHEST_EDUCATION.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.highestEducation}
								onChange={(event) =>
									updateField("highestEducation", event.target.value)
								}
								placeholder="Select"
							>
								{HIGHEST_EDUCATION_OPTIONS.map((opt) => (
									<option key={opt} value={opt}>
										{opt}
									</option>
								))}
							</Select>
							{maybeErrorText(errors.highestEducation)}
						</FormControl>
						<FormControl flex="1">
							<Label>{FIELD_LABELS.COLLEGE_UNIVERSITY.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.collegeOrUniversity}
								onChange={(event) =>
									updateField("collegeOrUniversity", event.target.value)
								}
								placeholder="e.g. Pune University"
								type="text"
							/>
						</FormControl>
					</FieldGrid>
				</Box>
			</Box>

			<Box {...sectionCardProps}>
				<SectionHeader title="CAREER" />
				<Box mt={3}>
					<FieldGrid>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.employmentSector)}
						>
							<Label>{FIELD_LABELS.EMPLOYMENT_SECTOR.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.employmentSector}
								onChange={(event) =>
									updateField("employmentSector", event.target.value)
								}
								placeholder="Select your employment sector"
							>
								{EMPLOYMENT_SECTOR_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
							{maybeErrorText(errors.employmentSector)}
						</FormControl>

						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.designation)}
						>
							<Label>{FIELD_LABELS.DESIGNATION.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.designation}
								onChange={(event) =>
									updateField("designation", event.target.value)
								}
								placeholder="e.g. Senior Engineer"
							/>
							{maybeErrorText(errors.designation)}
						</FormControl>
					</FieldGrid>

					<FieldGrid>
						<FormControl flex="1">
							<Label>{FIELD_LABELS.EMPLOYER_NAME.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.employerName}
								onChange={(event) =>
									updateField("employerName", event.target.value)
								}
								placeholder="e.g. TATA Steel, Government or Self"
							/>
						</FormControl>
						<FormControl flex="1">
							<Label>{FIELD_LABELS.ANNUAL_INCOME.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.annualIncome}
								onChange={(event) =>
									updateField("annualIncome", event.target.value)
								}
								placeholder="e.g. 25,00,000"
								type="number"
							/>
						</FormControl>
					</FieldGrid>
				</Box>
			</Box>

			<Box {...sectionCardProps}>
				<SectionHeader title="SOCIAL MEDIA" />
				<Box mt={3}>
					<VStack align="stretch" spacing={3}>
						{links.map((l, idx) => (
							<HStack key={idx} spacing={3} align="flex-start">
								<FormControl flex="1">
									<Label>{FIELD_LABELS.PLATFORM.toUpperCase()}</Label>
									<Select
										{...commonSelectProps}
										value={l.platform || ""}
										onChange={(e) => updateLink(idx, { platform: e.target.value })}
										placeholder="Select"
									>
										{SOCIAL_PLATFORM_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</Select>
								</FormControl>

								<FormControl flex="2">
									<Label>{FIELD_LABELS.URL.toUpperCase()}</Label>
									<Input
										{...commonInputProps}
										value={l.url || ""}
										onChange={(e) => updateLink(idx, { url: e.target.value })}
										placeholder="https://..."
									/>
								</FormControl>

								<Button
									marginTop={7}
									variant="outline"
									borderColor="rgba(108, 117, 125, 0.3)"
									onClick={() => removeLink(idx)}
								>
									Remove
								</Button>
							</HStack>
						))}

						<Button
							variant="outline"
							borderColor="rgba(108, 117, 125, 0.3)"
							onClick={addLink}
						>
							Add Social Link
						</Button>
					</VStack>
				</Box>
			</Box>
		</VStack>
	);
};

export default Step2CareerEducation;
